import { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import toast from 'react-hot-toast';
import * as Icons from '@mui/icons-material';
import {
    Box, 
    Typography, 
    Button,
    Tooltip, 
    Card, 
    CardContent,
    CircularProgress, 
    useMediaQuery,
    Dialog, 
    DialogActions, 
    DialogContent,
    DialogTitle, 
    Divider, 
    FormControlLabel,
    Grid, 
    IconButton, 
    MenuItem,
    Select, 
    FormControl, 
    InputLabel, 
    Stack,
    Switch, 
    TextField,
} from '@mui/material';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';
import TiptapEditor from 'components/TiptapEditor';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';

/* ─── Constants ──────────────────────────────────────────────────────────── */

const genId = () => `${Date.now()}-${Math.floor(Math.random() * 9999)}`;

const SECTION_TYPES = [
    { value: 'hero',     label: 'Hero',      desc: 'Large banner with headline and CTA' },
    { value: 'stats',    label: 'Stats',     desc: 'Key metrics and numbers' },
    { value: 'features', label: 'Features',  desc: 'Grid of feature cards with icons' },
    { value: 'pricing',  label: 'Pricing',   desc: 'Pricing tiers table' },
    { value: 'faq',      label: 'FAQ',       desc: 'Accordion Q&A list' },
    { value: 'cta',      label: 'CTA',       desc: 'Call-to-action banner' },
    { value: 'richtext', label: 'Rich Text', desc: 'Free-form rich text block' },
    { value: 'footer',   label: 'Footer',    desc: 'Footer with copyright and links' },
];

const ICON_OPTIONS = [
    'Flight','Group','BarChart','Bolt','Schedule','Star','Shield','Security',
    'Layers','Dashboard','Analytics','TrendingDown','TrendingUp','Speed',
    'CheckCircle','Settings','Support','Payment','Cloud','Lock','Public','Build',
];

const DEFAULT_CONTENT = {
    hero:     { badge: '', headline: 'New Headline', subheadline: '', ctaLabel: 'Get Started', ctaLink: '/auth/login' },
    stats:    { items: [{ id: genId(), value: '100+', label: 'Stat label', icon: 'Group' }] },
    features: { headline: 'Features', subheadline: '', items: [{ id: genId(), icon: 'Star', title: 'Feature', description: '', isVisible: true }] },
    pricing:  { headline: 'Pricing', subheadline: '', tiers: [] },
    faq:      { headline: 'Frequently Asked Questions', items: [{ id: genId(), question: 'Question?', answer: 'Answer.', isVisible: true }] },
    cta:      { headline: 'Ready to get started?', body: '', buttonLabel: 'Contact Us', buttonLink: '/contact' },
    richtext: { html: '' },
    footer:   { copyright: `© ${new Date().getFullYear()} DigitAir. All rights reserved.`, links: [] },
};

/* ─── Small helpers ──────────────────────────────────────────────────────── */

const moveUp = (arr, i) => {
    if (i === 0) return arr;
    const next = [...arr];
    [next[i - 1], next[i]] = [next[i], next[i - 1]];
    return next;
};
const moveDown = (arr, i) => {
    if (i === arr.length - 1) return arr;
    const next = [...arr];
    [next[i], next[i + 1]] = [next[i + 1], next[i]];
    return next;
};

/* ─── Reorder controls (shared by all list editors) ─────────────────────── */

const ReorderControls = ({ idx, length, onMoveUp, onMoveDown, onDelete }) => (
    <Stack direction="row" spacing={0.5}>
        <IconButton size="small" onClick={onMoveUp}   disabled={idx === 0}><Icons.KeyboardArrowUp fontSize="small" /></IconButton>
        <IconButton size="small" onClick={onMoveDown} disabled={idx === length - 1}><Icons.KeyboardArrowDown fontSize="small" /></IconButton>
        <IconButton size="small" color="error" onClick={onDelete}><Icons.DeleteOutline fontSize="small" /></IconButton>
    </Stack>
);

/* ─── Type-specific content editors ─────────────────────────────────────── */

const HeroContent = ({ content, onChange }) => (
    <Stack spacing={2}>
        <TextField 
            fullWidth
            label="Badge text" 
            value={content.badge || ''} 
            size="small"
            onChange={e => onChange({ badge: e.target.value })} 
        />
        <TextField 
            label="Headline" 
            value={content.headline || ''} 
            size="small"
            multiline rows={2}
            onChange={e => onChange({ headline: e.target.value })} 
        />
        <TextField 
            label="Subheadline" 
            value={content.subheadline || ''} 
            size="small"
            multiline rows={3}
            onChange={e => onChange({ subheadline: e.target.value })} 
        />
        <Grid container spacing={2}>
            <Grid width={{ xs: '100%', sm: '50%' }}>
                <TextField 
                    fullWidth 
                    label="CTA Button Label" 
                    value={content.ctaLabel || ''} 
                    size="small"
                    onChange={e => onChange({ ctaLabel: e.target.value })} 
                />
            </Grid>
            <Grid width={{ xs: '100%', sm: '50%' }}>
                <TextField 
                    fullWidth 
                    label="CTA Button Link" 
                    value={content.ctaLink || ''} 
                    size="small"
                    onChange={e => onChange({ ctaLink: e.target.value })} 
                />
            </Grid>
        </Grid>
    </Stack>
);

const StatsContent = ({ content, onChange }) => {
    const items = content.items || [];
    const update = (idx, patch) => onChange({ items: items.map((it, i) => i === idx ? { ...it, ...patch } : it) });
    const add    = () => onChange({ items: [...items, { id: genId(), value: '', label: '', icon: 'Group' }] });
    const remove = (idx) => onChange({ items: items.filter((_, i) => i !== idx) });

    return (
        <Stack spacing={2}>
            {items.map((stat, idx) => (
                <Card key={stat.id} variant="outlined">
                    <CardContent>
                        <Stack spacing={1.5}>
                            <FlexBetween>
                                <Typography variant="caption" color="text.secondary">Stat #{idx + 1}</Typography>
                                <ReorderControls idx={idx} length={items.length}
                                    onMoveUp={() => onChange({ items: moveUp(items, idx) })}
                                    onMoveDown={() => onChange({ items: moveDown(items, idx) })}
                                    onDelete={() => remove(idx)}
                                />
                            </FlexBetween>
                            <Grid container spacing={1.5}>
                                <Grid width={{ xs: '50%', sm: '33.33%' }}>
                                    <TextField fullWidth size="small" label="Value" value={stat.value || ''} onChange={e => update(idx, { value: e.target.value })} />
                                </Grid>
                                <Grid width={{ xs: '50%', sm: '33.33%' }}>
                                    <TextField fullWidth size="small" label="Label" value={stat.label || ''} onChange={e => update(idx, { label: e.target.value })} />
                                </Grid>
                                <Grid width={{ xs: '100%', sm: '33.33%' }}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Icon</InputLabel>
                                        <Select value={stat.icon || 'Group'} label="Icon" onChange={e => update(idx, { icon: e.target.value })}>
                                            {ICON_OPTIONS.map(n => {
                                                const Ic = Icons[n];
                                                return <MenuItem key={n} value={n}><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>{Ic && <Ic fontSize="small" />}{n}</Box></MenuItem>;
                                            })}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </Stack>
                    </CardContent>
                </Card>
            ))}
            <Button startIcon={<Icons.AddCircleOutline />} onClick={add} variant="outlined" size="small" sx={{ alignSelf: 'flex-start' }}>
                Add Stat
            </Button>
        </Stack>
    );
};

const FeaturesContent = ({ content, onChange }) => {
    const items = content.items || [];
    const update = (idx, patch) => onChange({ items: items.map((it, i) => i === idx ? { ...it, ...patch } : it) });
    const add    = () => onChange({ items: [...items, { id: genId(), icon: 'Star', title: 'New Feature', description: '', isVisible: true }] });
    const remove = (idx) => onChange({ items: items.filter((_, i) => i !== idx) });

    return (
        <Stack spacing={2}>
            <TextField label="Section Headline" value={content.headline || ''} size="small"
                onChange={e => onChange({ headline: e.target.value })} />
            <TextField label="Section Subheadline" value={content.subheadline || ''} size="small"
                onChange={e => onChange({ subheadline: e.target.value })} />
            <Divider />
            {items.map((feat, idx) => (
                <Card key={feat.id} variant="outlined">
                    <CardContent>
                        <Stack spacing={1.5}>
                            <FlexBetween>
                                <FormControlLabel
                                    control={<Switch checked={feat.isVisible !== false} size="small" onChange={e => update(idx, { isVisible: e.target.checked })} />}
                                    label={<Typography variant="caption">Visible</Typography>}
                                />
                                <ReorderControls idx={idx} length={items.length}
                                    onMoveUp={() => onChange({ items: moveUp(items, idx) })}
                                    onMoveDown={() => onChange({ items: moveDown(items, idx) })}
                                    onDelete={() => remove(idx)}
                                />
                            </FlexBetween>
                            <Grid container spacing={1.5}>
                                <Grid width={{ xs: '100%', sm: '30%' }}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Icon</InputLabel>
                                        <Select value={feat.icon || 'Star'} label="Icon" onChange={e => update(idx, { icon: e.target.value })}>
                                            {ICON_OPTIONS.map(n => {
                                                const Ic = Icons[n];
                                                return <MenuItem key={n} value={n}><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>{Ic && <Ic fontSize="small" />}{n}</Box></MenuItem>;
                                            })}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid width={{ xs: '100%', sm: '70%' }}>
                                    <TextField fullWidth size="small" label="Title" value={feat.title || ''} onChange={e => update(idx, { title: e.target.value })} />
                                </Grid>
                            </Grid>
                            <TextField fullWidth size="small" label="Description" multiline rows={2} value={feat.description || ''} onChange={e => update(idx, { description: e.target.value })} />
                        </Stack>
                    </CardContent>
                </Card>
            ))}
            <Button startIcon={<Icons.AddCircleOutline />} onClick={add} variant="outlined" size="small" sx={{ alignSelf: 'flex-start' }}>
                Add Feature
            </Button>
        </Stack>
    );
};

const PricingContent = ({ content, onChange }) => {
    const tiers = content.tiers || [];
    const update = (idx, patch) => onChange({ tiers: tiers.map((t, i) => i === idx ? { ...t, ...patch } : t) });
    const add    = () => onChange({ tiers: [...tiers, { id: genId(), name: 'New Tier', price: null, unit: 'per month', isHighlighted: false, features: [] }] });
    const remove = (idx) => onChange({ tiers: tiers.filter((_, i) => i !== idx) });

    return (
        <Stack spacing={2}>
            <TextField label="Section Headline" value={content.headline || ''} size="small"
                onChange={e => onChange({ headline: e.target.value })} />
            <TextField label="Section Subheadline" value={content.subheadline || ''} size="small"
                onChange={e => onChange({ subheadline: e.target.value })} />
            <Divider />
            {tiers.map((tier, idx) => (
                <Card key={tier.id} variant="outlined">
                    <CardContent>
                        <Stack spacing={1.5}>
                            <FlexBetween>
                                <FormControlLabel
                                    control={<Switch checked={!!tier.isHighlighted} size="small" onChange={e => update(idx, { isHighlighted: e.target.checked })} />}
                                    label={<Typography variant="caption">Highlighted</Typography>}
                                />
                                <ReorderControls idx={idx} length={tiers.length}
                                    onMoveUp={() => onChange({ tiers: moveUp(tiers, idx) })}
                                    onMoveDown={() => onChange({ tiers: moveDown(tiers, idx) })}
                                    onDelete={() => remove(idx)}
                                />
                            </FlexBetween>
                            <Grid container spacing={1.5}>
                                <Grid width={{ xs: '100%', sm: '40%' }}>
                                    <TextField fullWidth size="small" label="Tier Name" value={tier.name || ''} onChange={e => update(idx, { name: e.target.value })} />
                                </Grid>
                                <Grid width={{ xs: '50%', sm: '25%' }}>
                                    <TextField fullWidth size="small" label="Price" type="number" value={tier.price ?? ''} onChange={e => update(idx, { price: e.target.value === '' ? null : Number(e.target.value) })} />
                                </Grid>
                                <Grid width={{ xs: '50%', sm: '35%' }}>
                                    <TextField fullWidth size="small" label="Unit (e.g. /month)" value={tier.unit || ''} onChange={e => update(idx, { unit: e.target.value })} />
                                </Grid>
                            </Grid>
                            <TextField
                                fullWidth size="small" label="Features (one per line)" multiline rows={4}
                                value={(tier.features || []).join('\n')}
                                onChange={e => update(idx, { features: e.target.value.split('\n') })}
                                helperText="Enter each feature on a new line"
                            />
                        </Stack>
                    </CardContent>
                </Card>
            ))}
            <Button startIcon={<Icons.AddCircleOutline />} onClick={add} variant="outlined" size="small" sx={{ alignSelf: 'flex-start' }}>
                Add Tier
            </Button>
        </Stack>
    );
};

const FAQContent = ({ content, onChange }) => {
    const items = content.items || [];
    const update = (idx, patch) => onChange({ items: items.map((it, i) => i === idx ? { ...it, ...patch } : it) });
    const add    = () => onChange({ items: [...items, { id: genId(), question: '', answer: '', isVisible: true }] });
    const remove = (idx) => onChange({ items: items.filter((_, i) => i !== idx) });
    return (
        <Stack spacing={2}>
            <TextField label="Section Headline" value={content.headline || ''} size="small"
                onChange={e => onChange({ headline: e.target.value })} />
            <Divider />
            {items.map((faq, idx) => (
                <Card key={faq.id} variant="outlined">
                    <CardContent>
                        <Stack spacing={1.5}>
                            <FlexBetween>
                                <FormControlLabel
                                    control={<Switch checked={faq.isVisible !== false} size="small" onChange={e => update(idx, { isVisible: e.target.checked })} />}
                                    label={<Typography variant="caption">Visible</Typography>}
                                />
                                <ReorderControls idx={idx} length={items.length}
                                    onMoveUp={() => onChange({ items: moveUp(items, idx) })}
                                    onMoveDown={() => onChange({ items: moveDown(items, idx) })}
                                    onDelete={() => remove(idx)}
                                />
                            </FlexBetween>
                            <TextField fullWidth size="small" label="Question" value={faq.question || ''} onChange={e => update(idx, { question: e.target.value })} />
                            <TextField fullWidth size="small" label="Answer" multiline rows={3} value={faq.answer || ''} onChange={e => update(idx, { answer: e.target.value })} />
                        </Stack>
                    </CardContent>
                </Card>
            ))}
            <Button startIcon={<Icons.AddCircleOutline />} onClick={add} variant="outlined" size="small" sx={{ alignSelf: 'flex-start' }}>
                Add Question
            </Button>
        </Stack>
    );
};

const CTAContent = ({ content, onChange }) => (
    <Stack spacing={2}>
        <TextField label="Headline" value={content.headline || ''} onChange={e => onChange({ headline: e.target.value })} />
        <TextField label="Body text" value={content.body || ''} multiline rows={3} onChange={e => onChange({ body: e.target.value })} />
        <Grid container spacing={2}>
            <Grid width={{ xs: '100%', sm: '50%' }}>
                <TextField fullWidth size="small" label="Button Label" value={content.buttonLabel || ''} onChange={e => onChange({ buttonLabel: e.target.value })} />
            </Grid>
            <Grid width={{ xs: '100%', sm: '50%' }}>
                <TextField fullWidth size="small" label="Button Link" value={content.buttonLink || ''} onChange={e => onChange({ buttonLink: e.target.value })} />
            </Grid>
        </Grid>
    </Stack>
);

const RichTextContent = ({ content, onChange }) => (
    <TiptapEditor
        content={content.html || ''}
        onChange={(html) => onChange({ html })}
        placeholder="Enter rich text content..."
        minHeight={300}
    />
);

const FooterContent = ({ content, onChange }) => {
    const links = content.links || [];
    const updateLink = (idx, patch) => onChange({ links: links.map((l, i) => i === idx ? { ...l, ...patch } : l) });
    const addLink    = () => onChange({ links: [...links, { id: genId(), label: '', url: '' }] });
    const removeLink = (idx) => onChange({ links: links.filter((_, i) => i !== idx) });
    return (
        <Stack spacing={2}>
            <TextField label="Copyright text" value={content.copyright || ''} onChange={e => onChange({ copyright: e.target.value })} />
            <Divider />
            <Typography variant="subtitle2">Links</Typography>
            {links.map((link, idx) => (
                <Grid key={link.id || idx} container spacing={1.5} alignItems="center">
                    <Grid width={{ xs: '45%', sm: '40%' }}>
                        <TextField fullWidth size="small" label="Label" value={link.label || ''} onChange={e => updateLink(idx, { label: e.target.value })} />
                    </Grid>
                    <Grid width={{ xs: '45%', sm: '50%' }}>
                        <TextField fullWidth size="small" label="URL" value={link.url || ''} onChange={e => updateLink(idx, { url: e.target.value })} />
                    </Grid>
                    <Grid width={{ xs: '10%', sm: 'auto' }} sx={{ display: 'flex', justifyContent: 'center' }}>
                        <IconButton size="small" color="error" onClick={() => removeLink(idx)}><Icons.DeleteOutline fontSize="small" /></IconButton>
                    </Grid>
                </Grid>
            ))}
            <Button startIcon={<Icons.AddCircleOutline />} onClick={addLink} variant="outlined" size="small" sx={{ alignSelf: 'flex-start' }}>
                Add Link
            </Button>
        </Stack>
    );
};

/* ─── Content editor dispatcher ─────────────────────────────────────────── */
const ContentEditor = ({ type, content, onChange }) => {
    switch (type) {
        case 'hero':     return <HeroContent      content={content} onChange={onChange} />;
        case 'stats':    return <StatsContent     content={content} onChange={onChange} />;
        case 'features': return <FeaturesContent  content={content} onChange={onChange} />;
        case 'pricing':  return <PricingContent   content={content} onChange={onChange} />;
        case 'faq':      return <FAQContent       content={content} onChange={onChange} />;
        case 'cta':      return <CTAContent       content={content} onChange={onChange} />;
        case 'richtext': return <RichTextContent  content={content} onChange={onChange} />;
        case 'footer':   return <FooterContent    content={content} onChange={onChange} />;
        default: return <Typography color="text.secondary">Unknown section type.</Typography>;
    }
};

/* ─── Main Editor ────────────────────────────────────────────────────────── */
export default function HomepageEditor() {
    useAuthCheck();
    const navigate = useNavigate();
    const isMobile = useMediaQuery('(max-width: 600px)');

    const [sections,        setSections]        = useState([]);
    const [activeTab,       setActiveTab]       = useState(0);
    const [addDialogOpen,   setAddDialogOpen]   = useState(false);
    const [resetDialogOpen, setResetDialogOpen] = useState(false);
    const [pendingType,     setPendingType]     = useState('hero');
    const [isLoading,       setIsLoading]       = useState(true);
    const [isSaving,        setIsSaving]        = useState(false);
    const [isResetting,     setIsResetting]     = useState(false);

    useEffect(() => {
        api.get('/homepageconfig')
            .then(res => {
                    const data = res.data;

                    if (data?.sections) {
                        setSections([...data.sections].sort((a, b) => a.order - b.order));
                    }
            })
            .catch(() => toast.error('Failed to load homepage config.'))
            .finally(() => setIsLoading(false));
    }, []);

    /* ── Section-level operations ── */

    const updateSection = (idx, patch) =>
        setSections(prev => prev.map((s, i) => i === idx ? { ...s, ...patch } : s));

    const updateContent = (idx, contentPatch) =>
        setSections(prev => prev.map((s, i) =>
            i === idx ? { ...s, content: { ...s.content, ...contentPatch } } : s
        ));

    const handleDelete = (idx) => {
        setSections(prev => prev.filter((_, i) => i !== idx));
        setActiveTab(t => Math.max(0, t > idx ? t - 1 : Math.min(t, sections.length - 2)));
    };

    const handleMoveUp   = (idx) => { setSections(prev => moveUp(prev, idx));   setActiveTab(idx - 1); };
    const handleMoveDown = (idx) => { setSections(prev => moveDown(prev, idx)); setActiveTab(idx + 1); };

    const handleAdd = () => {
        const newSection = {
            id: genId(), type: pendingType, label: SECTION_TYPES.find(t => t.value === pendingType)?.label || pendingType,
            order: sections.length, isVisible: true, showInNav: false,
            content: { ...(DEFAULT_CONTENT[pendingType] || {}) },
        };
        setSections(prev => [...prev, newSection]);
        setActiveTab(sections.length);
        setAddDialogOpen(false);
    };

    const handleTypeChange = (idx, newType) => {
        setSections(prev => prev.map((s, i) =>
            i === idx ? { ...s, type: newType, content: { ...(DEFAULT_CONTENT[newType] || {}) } } : s
        ));
    };

    /* ── Save / Reset ── */

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const payload = sections.map((s, i) => ({ ...s, order: i }));
            await api.put('/homepageconfig', { sections: payload });

            toast.success('Homepage saved successfully.');
        } catch {
            toast.error('Failed to save. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleReset = async () => {
        setIsResetting(true);
        try {
            const res = await api.post('/homepageconfig/reset');
            const result = res.data;

            if (result?.sections) {
                setSections([...result.sections].sort((a, b) => a.order - b.order));
                setActiveTab(0);
            }
            
            setResetDialogOpen(false);
            toast.success('Homepage reset to defaults.');
        } catch {
            toast.error('Reset failed.');
        } finally {
            setIsResetting(false);
        }
    };

    if (isLoading) {
        return (
            <Box className="main-container-box">
                <Header title="Home Page Editor" subtitle="Loading..." />
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <CircularProgress />
                </FlexCenter>
            </Box>
        );
    }

    const activeSection = sections[activeTab];

    return (
        <Box className="main-container-box">
            {/* ── Header ── */}
            <FlexBetween mb={2}>
                <Header title="Home Page Editor" subtitle="Manage page content" />
                <Stack direction="row" spacing={1}>
                    <Tooltip title="Reset to Defaults" arrow>
                        <span>
                            <StyledButton onClick={() => setResetDialogOpen(true)}>
                                <Icons.Refresh />
                            </StyledButton>
                        </span>
                    </Tooltip>
                    <Tooltip title="Preview">
                        <span>
                            <StyledButton onClick={() => navigate("/")}>
                                <Icons.OpenInNew />
                            </StyledButton>
                        </span>
                    </Tooltip>
                    <Tooltip title={isSaving ? 'Saving…' : 'Save'}>
                        <span>
                            <StyledButton 
                                disabled={isSaving}
                                onClick={handleSave} 
                            >
                                <Icons.Save />
                            </StyledButton>
                        </span>                        
                    </Tooltip>
                </Stack>
            </FlexBetween>

            {/* ── Tab Bar ── */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, pb: 0.5 }}>
                    {sections.map((s, i) => {
                        const isActive = i === Math.min(activeTab, Math.max(0, sections.length - 1));
                        return (
                            <Box
                                key={s.id}
                                onClick={() => setActiveTab(i)}
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: 0.5,
                                    px: 1.5, py: 0.75,
                                    borderRadius: '6px 6px 0 0',
                                    cursor: 'pointer',
                                    userSelect: 'none',
                                    borderBottom: isActive ? '2px solid' : '2px solid transparent',
                                    borderBottomColor: isActive ? 'primary.main' : 'transparent',
                                    color: isActive ? 'primary.main' : 'text.secondary',
                                    bgcolor: isActive ? 'action.selected' : 'transparent',
                                    '&:hover': { bgcolor: 'action.hover' },
                                    fontSize: '0.8125rem',
                                    fontWeight: isActive ? 600 : 400,
                                    transition: 'all 0.15s',
                                }}
                            >
                                {!s.isVisible && (
                                    <Tooltip title="Hidden on public page">
                                        <Icons.VisibilityOff sx={{ fontSize: 13, color: 'text.disabled' }} />
                                    </Tooltip>
                                )}
                                <span>{s.label || s.type}</span>
                                <IconButton
                                    size="small" component="span"
                                    onClick={(e) => { e.stopPropagation(); handleDelete(i); }}
                                    sx={{ ml: 0.25, p: 0.25, '&:hover': { color: 'error.main' } }}
                                >
                                    <Icons.Close sx={{ fontSize: 13 }} />
                                </IconButton>
                            </Box>
                        );
                    })}

                    {/* Add Section button */}
                    {isMobile ? (
                        <Tooltip title="Add Section">
                            <IconButton size="small" onClick={() => setAddDialogOpen(true)}>
                                <Icons.Add />
                            </IconButton>
                        </Tooltip>
                    ) : (
                        <Button variant="text" size="small" startIcon={<Icons.Add />}
                            onClick={() => setAddDialogOpen(true)}
                            sx={{ borderRadius: '6px 6px 0 0', px: 1.5, py: 0.75, alignSelf: 'flex-end' }}
                        >
                            Add Section
                        </Button>
                    )}
                </Box>
            </Box>

            {/* ── Tab Panel ── */}
            {sections.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 10 }}>
                    <Icons.WebAsset sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                    <Typography color="text.secondary">No sections yet. Add your first section above.</Typography>
                </Box>
            ) : activeSection ? (
                <Box>
                    {/* Section meta settings */}
                    <Card variant="outlined" sx={{ mb: 2 }}>
                        <CardContent sx={{ p: { xs: 1.5, sm: 1.5 } }}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Section Settings
                            </Typography>
                            <Grid container spacing={1.5} alignItems="center">
                                <Grid width={{ xs: '100%', sm: '30%' }}>
                                    <TextField
                                        fullWidth size="small" label="Tab Label"
                                        value={activeSection.label || ''}
                                        onChange={e => updateSection(activeTab, { label: e.target.value })}
                                    />
                                </Grid>
                                <Grid width={{ xs: '100%', sm: '25%' }}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Section Type</InputLabel>
                                        <Select
                                            value={activeSection.type}
                                            label="Section Type"
                                            onChange={e => handleTypeChange(activeTab, e.target.value)}
                                        >
                                            {SECTION_TYPES.map(t => (
                                                <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                {/* Toggles + move buttons — inline on desktop, wrapped row on mobile */}
                                <Grid width={{ xs: '100%', sm: 'auto' }}>
                                    <Stack direction="row" spacing={0} flexWrap="wrap" alignItems="center">
                                        <FormControlLabel
                                            control={
                                                <Switch size="small"
                                                    checked={activeSection.isVisible !== false}
                                                    onChange={e => updateSection(activeTab, { isVisible: e.target.checked })}
                                                />
                                            }
                                            label={<Typography variant="body2">Visible</Typography>}
                                            sx={{ mr: 2 }}
                                        />
                                        <FormControlLabel
                                            control={
                                                <Switch size="small"
                                                    checked={!!activeSection.showInNav}
                                                    onChange={e => updateSection(activeTab, { showInNav: e.target.checked })}
                                                />
                                            }
                                            label={<Typography variant="body2">Nav bar</Typography>}
                                            sx={{ mr: 2 }}
                                        />
                                        <Stack direction="row" spacing={0.5}>
                                            <Tooltip title="Move up">
                                                <span>
                                                    <IconButton size="small" disabled={activeTab === 0} onClick={() => handleMoveUp(activeTab)}>
                                                        <Icons.KeyboardArrowUp />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            <Tooltip title="Move down">
                                                <span>
                                                    <IconButton size="small" disabled={activeTab === sections.length - 1} onClick={() => handleMoveDown(activeTab)}>
                                                        <Icons.KeyboardArrowDown />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </Stack>
                                    </Stack>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* Content editor */}
                    <ContentEditor
                        type={activeSection.type}
                        content={activeSection.content || {}}
                        onChange={(patch) => updateContent(activeTab, patch)}
                    />
                </Box>
            ) : null}

            {/* ── Add Section Dialog ── */}
            <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)}
                maxWidth="sm" fullWidth fullScreen={isMobile}
            >
                <DialogTitle>Add Section</DialogTitle>
                <DialogContent>
                    <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                        {SECTION_TYPES.map(t => (
                            <Grid key={t.value} width={{ xs: '100%', sm: '50%' }}>
                                <Card
                                    variant="outlined"
                                    onClick={() => setPendingType(t.value)}
                                    sx={{
                                        cursor: 'pointer', p: 1.5,
                                        border: pendingType === t.value ? 2 : 1,
                                        borderColor: pendingType === t.value ? 'primary.main' : 'divider',
                                        bgcolor: pendingType === t.value ? 'action.selected' : 'background.paper',
                                        transition: 'border-color 0.15s',
                                    }}
                                >
                                    <Typography variant="subtitle2">{t.label}</Typography>
                                    <Typography variant="caption" color="text.secondary">{t.desc}</Typography>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleAdd}>Add Section</Button>
                </DialogActions>
            </Dialog>

            {/* ── Reset Confirmation Dialog ── */}
            <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)}>
                <DialogTitle>Reset to Defaults?</DialogTitle>
                <DialogContent>
                    <Typography>This will replace all current content with the default homepage layout. This action cannot be undone.</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setResetDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleReset} disabled={isResetting}>
                        {isResetting ? 'Resetting…' : 'Reset'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Mobile sticky Save bar ── */}
            {isMobile && (
                <Box sx={{
                    position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1100,
                    bgcolor: 'background.paper',
                    borderTop: 1, borderColor: 'divider',
                    px: 2, py: 1.5,
                    display: 'flex', gap: 1,
                }}>
                    <Button fullWidth variant="outlined" size="small" color="inherit"
                        startIcon={<Icons.Refresh />}
                        onClick={() => setResetDialogOpen(true)}
                    >
                        Reset
                    </Button>
                    <Button fullWidth variant="contained" size="small"
                        onClick={handleSave} disabled={isSaving}
                        startIcon={isSaving ? undefined : <Icons.Save />}
                    >
                        {isSaving ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : 'Save'}
                    </Button>
                </Box>
            )}

            {/* Bottom padding so sticky bar doesn't cover content on mobile */}
            {isMobile && <Box sx={{ pb: 8 }} />}
        </Box>
    );
}
